import { z } from 'zod';
import { CONTRACT_TYPES, ONBOARDING_STEPS } from '../constants.js';
import { currencySchema, emailSchema, ianaTimezoneSchema, isoDateSchema, paginationQuerySchema, tenantSlugSchema, } from './common.js';
/** Platform console contracts (docs/07, docs/10 "/platform"). */
export const createTenantSchema = z.object({
    name: z.string().trim().min(2).max(120),
    slug: tenantSlugSchema,
    timezone: ianaTimezoneSchema,
    currency: currencySchema,
    owner: z.object({
        name: z.string().trim().min(1).max(120),
        email: emailSchema,
    }),
    contract: z
        .object({
        type: z.enum(CONTRACT_TYPES),
        commissionBps: z.number().int().min(0).max(10_000).optional(),
        monthlyFeeCents: z.number().int().min(0).max(100_000_000).optional(),
        effectiveFrom: isoDateSchema,
        notes: z.string().trim().max(1000).optional(),
    })
        .superRefine((contract, ctx) => {
        // Each type must carry the terms it bills on — the database enforces this
        // too, but a clear message beats a constraint violation.
        const needsCommission = contract.type === 'COMMISSION' || contract.type === 'HYBRID';
        const needsFee = contract.type === 'MONTHLY' || contract.type === 'HYBRID';
        if (needsCommission && contract.commissionBps === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['commissionBps'],
                message: 'A commission rate is required for this contract type',
            });
        }
        if (needsFee && contract.monthlyFeeCents === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['monthlyFeeCents'],
                message: 'A monthly fee is required for this contract type',
            });
        }
    }),
});
export const newContractSchema = z
    .object({
    type: z.enum(CONTRACT_TYPES),
    commissionBps: z.number().int().min(0).max(10_000).optional(),
    monthlyFeeCents: z.number().int().min(0).max(100_000_000).optional(),
    currency: currencySchema.optional(),
    effectiveFrom: isoDateSchema,
    notes: z.string().trim().max(1000).optional(),
})
    .superRefine((contract, ctx) => {
    const needsCommission = contract.type === 'COMMISSION' || contract.type === 'HYBRID';
    const needsFee = contract.type === 'MONTHLY' || contract.type === 'HYBRID';
    if (needsCommission && contract.commissionBps === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['commissionBps'],
            message: 'A commission rate is required for this contract type',
        });
    }
    if (needsFee && contract.monthlyFeeCents === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['monthlyFeeCents'],
            message: 'A monthly fee is required for this contract type',
        });
    }
});
export const onboardingStepSchema = z.object({
    key: z.enum(ONBOARDING_STEPS),
    done: z.boolean(),
});
export const addDomainSchema = z.object({
    domain: z
        .string()
        .trim()
        .toLowerCase()
        .min(4)
        .max(253)
        .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/, 'Enter a valid domain name'),
});
export const suspendSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
export const tenantListQuerySchema = paginationQuerySchema.extend({
    status: z.enum(['ONBOARDING', 'ACTIVE', 'SUSPENDED']).optional(),
    query: z.string().trim().max(120).optional(),
});
export const invoiceListQuerySchema = paginationQuerySchema.extend({
    status: z.enum(['DRAFT', 'SENT', 'PAID', 'VOID']).optional(),
    tenantId: z.string().trim().max(64).optional(),
});
export const generateInvoiceSchema = z.object({
    /** First day of the period, YYYY-MM-DD. Defaults to last month. */
    periodStart: isoDateSchema.optional(),
});
export const auditQuerySchema = paginationQuerySchema.extend({
    tenantId: z.string().trim().max(64).optional(),
    actorUserId: z.string().trim().max(64).optional(),
    entityType: z.string().trim().max(60).optional(),
});
// --- platform staff ---------------------------------------------------------
export const platformStaffInviteSchema = z.object({
    email: emailSchema,
    name: z.string().trim().min(1).max(120),
});
export const platformStaffUpdateSchema = z.object({
    active: z.boolean().optional(),
});
//# sourceMappingURL=platform.js.map